import { writable } from 'svelte/store';
import { fetchGet, vvSettings, getApplicationSettings, type ApplicationSettings, type UserProfileSettings } from './common';
import {getUser, type User} from './users';
export interface AddressesResponse {
    addresses: Address[],
    message: string | null,
    suggestedZipCode: string | null,
};

export interface AddressCollection {
    selectedAddress?: Address,
    addresses: Map<string, Address>
}

export interface Address {
    alternateCities?: { Key: string, Value: boolean }[],
    checksum?: string,
    city?: string,
    coordinates?: {
        isWeakCoordinates: boolean,
        latitude: number,
        longitude: number,
    } | null,
    country?: string,
    county?: string,
    state?: string,
    streetAddress?: string,
    zipCode?: string,
    zipCodeExtension?: string,
    addressType?: string,
};

export const checkAddress = async (streetAddress: string, zipCode: string, city?: string, state?: string) => {
    const canadaPostalCodePattern = /^[A-Za-z]\d[A-Za-z] ?\d[A-Za-z]\d$/;
    const country = canadaPostalCodePattern.test(zipCode) ? 'CA' : 'US';
    
    let cityString = !city ? '' : city;
    let stateString = !state ? '' : state;
    let cityCountryString = country == `US` ? `&city=${cityString}&state=${stateString}` : '';
    const url = `Addresses?address1=${encodeURIComponent(streetAddress)}${cityCountryString}&zipCode=${encodeURIComponent(zipCode)}&country=${country}`;
    const addressesResponse = await fetchGet<AddressesResponse>(url);
    return addressesResponse;
}

export const localStorageAddressKey = 'vv-usersite-address';

export const canadaProvincesMap: any = {
    ab: 'a',
    bc: 'b',
    mb: 'c',
    nb: 'd',
    nl: 'e',
    ns: 'f',
    nt: 'g',
    nu: 'h',
    on: 'i',
    pe: 'j',
    qc: 'k',
    sk: 'l',
    yt: 'm',
};

export const addressStore = writable<AddressCollection>({selectedAddress: {}, addresses: new Map()});

export const getSavedAddresses = (key?: string) => {
    try {
        const str = localStorage.getItem(key ?? localStorageAddressKey);
        let addresses : AddressCollection = { selectedAddress: undefined, addresses: new Map() };
        if (str) {
            let obj = JSON.parse(str);
            if (obj.addresses && obj.addresses.length > 0) {
                obj.addresses.forEach((address: Address) => {
                    if (address.addressType) {
                        addresses.addresses.set(address.addressType, address);
                    }
                });
            } else {
                clearAddress();
            }
            
            if (obj.selectedAddress) {
                addresses.selectedAddress = obj.selectedAddress;
            }
        }
        
        return addresses;
        
    } catch (error) {
        console.error("Error parsing saved address from localStorage", error);
        let addresses : AddressCollection = { selectedAddress: undefined, addresses: new Map() };
        return addresses;
    }
}

export const saveAddress = (address: Address, key?: string) => {
    if (!address.addressType) {
        return;
    }
    
    const localStorageKey = key ?? localStorageAddressKey;
    const addressCollection = getSavedAddresses(localStorageKey);
    addressCollection.selectedAddress = address;
    addressCollection.addresses.set(address.addressType, address);
    
    saveAddresses(addressCollection, key);
}

export const saveAddresses = (addressCollection : AddressCollection, key? : string) => {
    if (!addressCollection)
        return;
    
    const localStorageKey = key ?? localStorageAddressKey;
    const obj = {
        selectedAddress:  addressCollection.selectedAddress,
        addresses:  Array.from(addressCollection.addresses, ([key, value]) => value)
    };

    localStorage.setItem(localStorageKey, JSON.stringify(obj));
}

export const clearAddress = (addressType? : string, key?: string) => {
    if (!addressType) {
        localStorage.removeItem(key ?? localStorageAddressKey);
        return;
    }
    
    const addressCollection = getSavedAddresses();
    addressCollection.addresses.delete(addressType);
    if (addressCollection.selectedAddress?.addressType === addressType) {
        addressCollection.selectedAddress = undefined;
    }
    
    saveAddresses(addressCollection, key);
}

export const getResolvedAddresses = async (searchParams? : URLSearchParams, userInfo? : User, applicationType? : string) => {
    let address : Address | undefined;
    const applicationSettings : ApplicationSettings = await getApplicationSettings(applicationType);
    const userAddressType = applicationSettings.userProfile.preferredAddressType;

    let savedAddressCollection : AddressCollection = getSavedAddresses();
    
    if (!savedAddressCollection) {
        savedAddressCollection = { selectedAddress: undefined, addresses: new Map() };
    }
    
    if (searchParams && searchParams.has('checksum')) {
        let searchParamAddressType = searchParams.get('addressType') ?? '';
        if (searchParamAddressType.toLowerCase() === 'business') {
            searchParamAddressType = 'B';
        } else if (searchParamAddressType.toLowerCase() === 'home') {
            searchParamAddressType = 'H';
        } else {
            searchParamAddressType = userAddressType;
        }
        address = {
            addressType: searchParamAddressType,
            checksum: searchParams.get('checksum') ?? '',
            city: searchParams.get('city') ?? '',
            coordinates: {
                latitude: Number.parseFloat(searchParams.get('latitude') ?? ''),
                longitude: Number.parseFloat(searchParams.get('longitude') ?? ''),
                isWeakCoordinates: false,
            },
            country: searchParams.get('country') ?? '',
            county: searchParams.get('county') ?? '',
            state: searchParams.get('state') ?? '',
            streetAddress: searchParams.get('streetAddress') ?? '',
            zipCode: searchParams.get('zipCode') ?? '',
            zipCodeExtension: searchParams.get('zipCodeExtension') ?? '',
        };
        
        savedAddressCollection.selectedAddress = address;
        savedAddressCollection.addresses.set(searchParamAddressType, address);
    } 
    
    if (!savedAddressCollection.addresses.get('H') && userInfo?.homeAddress?.zipCode !== undefined && userInfo?.homeAddress?.zipCode !== null) {
        userInfo.homeAddress.addressType = 'H';
        savedAddressCollection.addresses.set('H', userInfo.homeAddress);
    }

    if (!savedAddressCollection.addresses.get('B') && userInfo?.businessAddress?.zipCode !== undefined && userInfo?.businessAddress?.zipCode !== null) {
        userInfo.businessAddress.addressType = 'B';
        savedAddressCollection.addresses.set('B', userInfo.businessAddress);
    }
    
    if (vvSettings.address) {
        if (vvSettings.address.addressType && !savedAddressCollection.addresses.get(vvSettings.address.addressType)) {
            savedAddressCollection.addresses.set(vvSettings.address.addressType, vvSettings.address);
        } else  if (!savedAddressCollection.addresses.get(userAddressType)) {
            savedAddressCollection.addresses.set(userAddressType, vvSettings.address);
        }
    }
    
    if (!savedAddressCollection.selectedAddress && savedAddressCollection.addresses.get(userAddressType)) {
        savedAddressCollection.selectedAddress = savedAddressCollection.addresses.get(userAddressType);
    }
    
    return savedAddressCollection;
}

export const addressNeedsGeocoding = (address: Address) => {
    return !(address?.checksum && address?.coordinates && address?.zipCodeExtension);
}
